import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Lock, Unlock, Plus, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RedButton, GhostButton } from "@/components/Layout";

const STATE_STYLE = {
  free: "border-emerald-600/60 bg-emerald-50 text-emerald-800",
  booked: "border-sared bg-sared/10 text-sared",
  pending: "border-amber-500/70 bg-amber-50 text-amber-800",
  blocked: "border-zinc-300 bg-zinc-100 text-zinc-500",
  past: "border-zinc-200 bg-zinc-50 text-zinc-400",
};
const STATE_LABEL = { free: "LIBRE", booked: "RÉSERVÉ", pending: "EN ATTENTE", blocked: "BLOQUÉ", past: "PASSÉ" };

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "", plate: "", vehicle_brand: "", vehicle_model: "",
  service_text: "", description: "", price: "", internal_notes: "", status: "confirmed", notify_client: true,
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -((d.getDay() + 6) % 7));
const frDate = (s) => new Date(s + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">{label}</span>
    <input data-testid={testid}
      className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none"
      {...props} />
  </label>
);

export default function AdminAgenda() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);

  const range = useCallback(() => {
    if (view === "day") return { start: iso(anchor), count: 1 };
    if (view === "week") return { start: iso(startOfWeek(anchor)), count: 7 };
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: iso(first), count: last.getDate() };
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { start, count } = range();
      const { data } = await api.get(`/admin/planning/days?start=${start}&count=${count}`);
      setDays(data.days);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const step = (dir) => setAnchor(view === "month"
    ? new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1)
    : addDays(anchor, dir * (view === "week" ? 7 : 1)));

  const onSlot = (day, slot) => {
    if (slot.appointment) { setDetail(slot.appointment); return; }
    if (slot.state === "past") return;
    setForm({ ...EMPTY, date: day.date, start: slot.start, end: slot.end });
  };

  const save = async () => {
    if (!form.last_name.trim() || !form.phone.trim() || !form.service_text.trim()) {
      toast.error("Nom, téléphone et prestation sont obligatoires");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/admin/appointments", payload);
      toast.success("Rendez-vous enregistré");
      setForm(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const patch = async (ref, data, msg) => {
    try { await api.patch(`/admin/appointments/${ref}`, data); toast.success(msg); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const removeAppt = async (ref) => {
    if (!window.confirm("Supprimer définitivement ce rendez-vous ?")) return;
    try { await api.delete(`/admin/appointments/${ref}`); toast.success("Supprimé"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleBlock = async (day, slot) => {
    try {
      if (slot.state === "blocked") await api.delete(`/admin/blocks?date=${day.date}&start=${slot.start}`);
      else await api.post("/admin/blocks", { date: day.date, start: slot.start });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const title = view === "month"
    ? anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : view === "week" ? `Semaine du ${frDate(iso(startOfWeek(anchor)))}` : frDate(iso(anchor));

  return (
    <div data-testid="admin-agenda">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => step(-1)} data-testid="agenda-prev" className="border border-zinc-800 p-2 text-zinc-300 hover:border-sared"><ChevronLeft size={16} /></button>
          <button onClick={() => step(1)} data-testid="agenda-next" className="border border-zinc-800 p-2 text-zinc-300 hover:border-sared"><ChevronRight size={16} /></button>
          <span className="font-head text-xl text-white capitalize" data-testid="agenda-title">{title}</span>
          <button onClick={() => setAnchor(new Date())} data-testid="agenda-today"
                  className="font-mono-tech text-[10px] tracking-[0.18em] text-sared hover:text-white">AUJOURD'HUI</button>
        </div>
        <div className="flex gap-2">
          {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`agenda-view-${v}`}
                    className={`font-head text-sm uppercase tracking-wider px-4 py-2 border ${
                      view === v ? "bg-sared border-sared text-white" : "border-zinc-800 text-zinc-300 hover:border-sared"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-5 font-mono-tech text-[9px] tracking-[0.16em] text-zinc-400">
        {Object.entries(STATE_LABEL).filter(([k]) => k !== "past").map(([k, l]) => (
          <span key={k} className="flex items-center gap-2"><span className={`inline-block h-3 w-3 border ${STATE_STYLE[k]}`} />{l}</span>
        ))}
      </div>

      {loading ? <p className="text-zinc-500">Chargement...</p> : (
        <div className={view === "day" ? "" : "grid gap-4 md:grid-cols-2 xl:grid-cols-3"}>
          {days.map((day) => (
            <div key={day.date} className="border border-zinc-800 bg-[#0a0a0a] p-4" data-testid={`agenda-day-${day.date}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-head text-lg text-white capitalize">{frDate(day.date)}</span>
                {!day.open && <span className="font-mono-tech text-[9px] tracking-[0.16em] text-sared">{day.reason || "FERMÉ"}</span>}
              </div>
              <div className="mt-3 space-y-2">
                {day.slots.map((slot) => (
                  <div key={slot.start} className={`flex items-center gap-2 border px-3 py-2 ${STATE_STYLE[slot.state]}`}
                       data-testid={`agenda-slot-${day.date}-${slot.start}`} data-state={slot.state}>
                    <button onClick={() => onSlot(day, slot)} className="flex-1 text-left"
                            data-testid={`agenda-open-${day.date}-${slot.start}`}>
                      <span className="font-mono-tech text-[10px] tracking-[0.14em]">{slot.start} – {slot.end}</span>
                      <span className="ml-2 font-head text-sm">
                        {slot.appointment ? `${slot.appointment.last_name} · ${slot.appointment.service_text}` : STATE_LABEL[slot.state]}
                      </span>
                    </button>
                    {!slot.appointment && slot.state !== "past" && (
                      <button onClick={() => toggleBlock(day, slot)} data-testid={`agenda-block-${day.date}-${slot.start}`}
                              title={slot.state === "blocked" ? "Libérer" : "Bloquer"} className="text-zinc-400 hover:text-sared">
                        {slot.state === "blocked" ? <Unlock size={13} /> : <Lock size={13} />}
                      </button>
                    )}
                    {!slot.appointment && slot.state === "free" && (
                      <button onClick={() => onSlot(day, slot)} className="text-zinc-400 hover:text-sared"
                              data-testid={`agenda-add-${day.date}-${slot.start}`}><Plus size={14} /></button>
                    )}
                  </div>
                ))}
                {day.slots.length === 0 && <p className="text-xs text-zinc-600">Aucun créneau ce jour</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 bg-black/80 overflow-y-auto p-4 md:p-10" data-testid="agenda-form">
          <div className="mx-auto max-w-3xl border border-sared bg-[#0a0a0a] p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline mb-1">Nouveau rendez-vous</div>
                <h3 className="font-head text-2xl text-white capitalize">{frDate(form.date)} · {form.start} – {form.end}</h3>
              </div>
              <button onClick={() => setForm(null)} data-testid="agenda-form-close" className="text-zinc-400 hover:text-sared"><X size={20} /></button>
            </div>
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <Field label="Nom" testid="ag-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              <Field label="Prénom" testid="ag-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Field label="Téléphone" testid="ag-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Field label="E-mail (facultatif)" testid="ag-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Field label="Immatriculation" testid="ag-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />
              <Field label="Marque" testid="ag-brand" value={form.vehicle_brand} onChange={(e) => setForm({ ...form, vehicle_brand: e.target.value })} />
              <Field label="Modèle" testid="ag-model" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} />
              <Field label="Prestation (texte libre)" testid="ag-service" value={form.service_text} onChange={(e) => setForm({ ...form, service_text: e.target.value })} />
              <Field label="Prix estimé (facultatif)" testid="ag-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              <label className="block">
                <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Statut</span>
                <select value={form.status} data-testid="ag-status" onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none">
                  <option value="confirmed">Confirmé</option>
                  <option value="pending">En attente</option>
                  <option value="done">Terminé</option>
                </select>
              </label>
            </div>
            <label className="block mt-5">
              <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Description de l'intervention</span>
              <textarea rows={3} data-testid="ag-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
            </label>
            <label className="block mt-5">
              <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Notes internes</span>
              <textarea rows={2} data-testid="ag-notes" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                        className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
            </label>
            <label className="mt-5 flex items-center gap-3 text-sm text-zinc-300">
              <input type="checkbox" data-testid="ag-notify" checked={form.notify_client}
                     onChange={(e) => setForm({ ...form, notify_client: e.target.checked })} />
              Envoyer l'e-mail de confirmation (si une adresse est renseignée)
            </label>
            <div className="mt-8 flex gap-4">
              <RedButton onClick={save} data-testid="ag-save">Enregistrer</RedButton>
              <GhostButton onClick={() => setForm(null)}>Annuler</GhostButton>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 overflow-y-auto p-4 md:p-10" data-testid="agenda-detail">
          <div className="mx-auto max-w-2xl border border-sared bg-[#0a0a0a] p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline mb-1">{detail.reference} · {detail.status}</div>
                <h3 className="font-head text-2xl text-white capitalize">{frDate(detail.date)} · {detail.start} – {detail.end}</h3>
              </div>
              <button onClick={() => setDetail(null)} data-testid="agenda-detail-close" className="text-zinc-400 hover:text-sared"><X size={20} /></button>
            </div>
            <div className="mt-6 grid gap-2 text-sm">
              {[["Client", `${detail.first_name} ${detail.last_name}`], ["Téléphone", detail.phone],
                ["E-mail", detail.email || "—"], ["Véhicule", `${detail.vehicle_brand || ""} ${detail.vehicle_model || ""}`.trim() || "—"],
                ["Immatriculation", detail.plate || "—"], ["Prestation", detail.service_text],
                ["Description", detail.description || "—"], ["Prix estimé", detail.price || "—"],
                ["Notes internes", detail.internal_notes || "—"], ["Origine", detail.source === "client" ? "Client" : "Garage"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-zinc-900 pb-1.5">
                  <span className="font-mono-tech text-[10px] tracking-[0.16em] text-zinc-500 uppercase">{k}</span>
                  <span className="text-white text-right">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field label="Déplacer — date" testid="agenda-move-date" type="date" defaultValue={detail.date}
                     onChange={(e) => setDetail({ ...detail, _newDate: e.target.value })} />
              <Field label="Déplacer — heure (ex. 14:00)" testid="agenda-move-start" defaultValue={detail.start}
                     onChange={(e) => setDetail({ ...detail, _newStart: e.target.value })} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <RedButton className="px-4 py-2.5 text-sm" data-testid="agenda-move-save"
                         onClick={() => patch(detail.reference, { date: detail._newDate || detail.date, start: detail._newStart || detail.start }, "Rendez-vous déplacé")}>
                Déplacer
              </RedButton>
              {detail.status === "pending" && (
                <GhostButton className="px-4 py-2.5 text-sm" data-testid="agenda-accept"
                             onClick={() => patch(detail.reference, { status: "confirmed" }, "Rendez-vous accepté")}>Accepter</GhostButton>
              )}
              <GhostButton className="px-4 py-2.5 text-sm" data-testid="agenda-done"
                           onClick={() => patch(detail.reference, { status: "done" }, "Marqué terminé")}>Terminé</GhostButton>
              <GhostButton className="px-4 py-2.5 text-sm" data-testid="agenda-cancel"
                           onClick={() => patch(detail.reference, { status: "cancelled" }, "Rendez-vous annulé")}>Annuler</GhostButton>
              <button onClick={() => removeAppt(detail.reference)} data-testid="agenda-delete"
                      className="px-3 py-2.5 border border-zinc-700 text-zinc-400 hover:border-sared hover:text-sared"><Trash2 size={16} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
